// /anniversaries/trash — the soft-deleted anniversaries (W39.54).
//
// The twin of `/wishes/trash`, and deliberately the same file shape: the two
// modules differ in what they call the row (`title` vs `name`) and in who may
// restore it (either partner, for an anchor), and both differences are
// normalised here rather than pushed into `components/system/TrashList.tsx`.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { TrashList, type TrashRow } from "@/components/system/TrashList";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import {
  listAnniversaryTrash,
  purgeAnniversary,
  restoreAnniversary,
} from "@/lib/api/anniversary";
import { queryKeys } from "@/lib/queries/keys";
import { ApiError } from "@/lib/api/pair";
import { appErrorMessage } from "@/lib/i18n/app";
import { ANNIVERSARY_RESTORE_DAYS } from "@/lib/retentionWindows";

export function AnniversaryTrashPage() {
  const qc = useQueryClient();

  const trash = useQuery({ queryKey: ["anniversary", "trash"], queryFn: listAnniversaryTrash });

  const afterChange = () => {
    void qc.invalidateQueries({ queryKey: ["anniversary", "trash"] });
    void qc.invalidateQueries({ queryKey: queryKeys.anniversary.all });
  };

  const restoreMut = useMutation({
    mutationFn: restoreAnniversary,
    onSuccess: afterChange,
  });
  const purgeMut = useMutation({
    mutationFn: purgeAnniversary,
    onSuccess: afterChange,
  });

  const rows: TrashRow[] = (trash.data ?? []).map((a) => ({
    id: a.id,
    label: a.title,
    daysLeft: a.days_left,
    canRestore: a.can_restore,
  }));

  return (
    <>
      <TrashList
        noun="紀念日垃圾桶"
        itemNoun="紀念日"
        parentTo="/anniversaries"
        parentLabel="紀念日"
        retentionDays={ANNIVERSARY_RESTORE_DAYS}
        rows={rows}
        isLoading={trash.isLoading}
        isError={trash.isError}
        errorMessage={
          trash.error instanceof ApiError
            ? appErrorMessage(trash.error.code, trash.error.message)
            : "載入垃圾桶失敗，請稍後再試。"
        }
        onRetry={() => void trash.refetch()}
        onRestore={async (id) => {
          await restoreMut.mutateAsync(id);
        }}
        onPurge={async (id) => {
          await purgeMut.mutateAsync(id);
        }}
      />
    </>
  );
}

export function AnniversaryTrashRoute() {
  return (
    <PageErrorBoundary label="紀念日垃圾桶">
      <AnniversaryTrashPage />
    </PageErrorBoundary>
  );
}
